import axios from 'axios'
import store from '../store'
import router from '../router'

// Error types for modular handling
export const ErrorTypes = {
  AUTHENTICATION: 'authentication',
  AUTHORIZATION: 'authorization',
  VALIDATION: 'validation',
  NETWORK: 'network',
  SERVER: 'server',
  NOT_FOUND: 'not_found',
  UNKNOWN: 'unknown'
}

// Error messages for different scenarios
export const ErrorMessages = {
  [ErrorTypes.AUTHENTICATION]: {
    default: 'Your session has expired. Please log in again.',
    invalidToken: 'Invalid authentication token. Please log in again.',
    noToken: 'No authentication token found. Please log in.',
    sessionExpired: 'Your session has expired. Please log in again.',
    login: 'Invalid credentials. Please check your email and password.'
  },
  [ErrorTypes.AUTHORIZATION]: {
    default: 'You do not have permission to perform this action.',
    adminRequired: 'This action requires admin privileges.',
    editRequired: 'This action requires edit privileges.',
    tenantAccess: 'You do not have access to this tenant.'
  },
  [ErrorTypes.VALIDATION]: {
    default: 'Please check your input and try again.',
    required: 'This field is required.',
    invalidFormat: 'Invalid format.',
    email: 'Invalid email format.',
    tenant: 'No tenant selected',
    load: 'Failed to load tenant data',
    save: 'Failed to save settings',
    login: 'Please check your email format and try again.'
  },
  [ErrorTypes.NETWORK]: {
    default: 'Network error. Please check your connection.',
    timeout: 'Request timed out. Please try again.',
    offline: 'You are offline. Please check your connection.'
  },
  [ErrorTypes.SERVER]: {
    default: 'Server error. Please try again later.',
    maintenance: 'Server is under maintenance. Please try again later.'
  },
  [ErrorTypes.NOT_FOUND]: {
    default: 'The requested resource was not found.',
    login: 'Invalid or expired login token.'
  },
  [ErrorTypes.UNKNOWN]: {
    default: 'An unexpected error occurred. Please try again.'
  }
}

// Helper to determine error type
const getErrorType = (error) => {
  if (!error.response) {
    return ErrorTypes.NETWORK
  }

  const status = error.response.status

  switch (status) {
    case 401:
      return ErrorTypes.AUTHENTICATION
    case 403:
      return ErrorTypes.AUTHORIZATION
    case 400:
      return ErrorTypes.VALIDATION
    case 404:
      return ErrorTypes.NOT_FOUND
    case 500:
    case 502:
    case 503:
    case 504:
      return ErrorTypes.SERVER
    default:
      return ErrorTypes.UNKNOWN
  }
}

// Helper to get appropriate error message
const getErrorMessage = (error) => {
  const errorType = getErrorType(error)
  const { message: serverMessage, errors, required_role } = error.response?.data || {}
  
  // Get the base message for the error type
  let message = ErrorMessages[errorType].default || 'An unexpected error occurred'
  
  // If there's a specific message from the server, use it
  if (serverMessage) {
    message = serverMessage
  }
  
  // Add additional context for specific error types
  switch (errorType) {
    case ErrorTypes.AUTHENTICATION:
      if (error.response?.status === 401) {
        message = ErrorMessages[errorType].sessionExpired || 'Your session has expired. Please log in again.'
      }
      break
    case ErrorTypes.AUTHORIZATION:
      if (required_role) {
        message = `${ErrorMessages[errorType].default} Required role: ${required_role}`
      }
      break
    case ErrorTypes.VALIDATION:
      if (errors) {
        const validationErrors = Object.entries(errors)
          .map(([field, fieldErrors]) => `${field}: ${fieldErrors.join(', ')}`)
          .join('; ')
        message = `${ErrorMessages[errorType].default} ${validationErrors}`
      }
      break
    case ErrorTypes.NETWORK:
      if (!navigator.onLine) {
        message = ErrorMessages[errorType].offline || 'You are currently offline. Please check your internet connection.'
      }
      break
  }
  
  return message
}

// Handle error based on type
const handleError = (error) => {
  const type = getErrorType(error)
  const message = getErrorMessage(error)

  // Only show notifications and take action for auth errors here.
  // All other errors are left to the calling component to handle,
  // preventing duplicate toast notifications.
  switch (type) {
    case ErrorTypes.AUTHENTICATION:
      store.dispatch('notification/showNotification', {
        type: 'error',
        message,
        duration: 5000
      })
      store.dispatch('logout')
      router.push('/login')
      break

    case ErrorTypes.NETWORK:
      store.dispatch('notification/showNotification', {
        type: 'error',
        message,
        duration: 5000
      })
      break
  }

  // Attach parsed error info so components can use it without re-parsing
  error._handled = type === ErrorTypes.AUTHENTICATION || type === ErrorTypes.NETWORK
  error._errorType = type
  error._errorMessage = message

  return Promise.reject(error)
}

// Setup axios interceptors
export const setupErrorHandling = () => {
  // Response interceptor
  axios.interceptors.response.use(
    response => response,
    error => handleError(error)
  )

  // Request interceptor
  axios.interceptors.request.use(
    config => {
      // Add auth token to all requests
      const token = store.state.token
      if (token) {
        config.headers.Authorization = `Bearer ${token}`
      }
      return config
    },
    error => {
      return Promise.reject(error)
    }
  )
}

// Export error handling utilities
export default {
  setupErrorHandling,
  getErrorType,
  getErrorMessage,
  ErrorTypes,
  ErrorMessages
} 